import React, { useState, useEffect } from 'react';
import { apiClient } from '../../../../api/client';
import {
  PRIMARY,
  TEXT_DARK,
  TEXT_MUTED,
  BORDER_LIGHT,
  BG_SURFACE,
  BG_CARD,
  FONT_HEADING,
} from '../designTokens';

interface Rule {
  id: string;
  name: string;
  ruleType: string;
  priority: number;
  enabled: boolean;
}

interface Order {
  id: string;
  purchaserName: string;
  referenceId: string;
  ticketCount: number;
}

interface FamilyGroupOption {
  id: string;
  name: string;
  active: boolean;
  memberCount: number;
  members: { id: string; fullName: string; ticketNumber: string | null }[];
}

interface PreviewData {
  totalGuestsInScope: number;
  guests: { id: string; fullName: string; ticketNumber: string }[];
  orderGroupsCount: number;
  familyGroupsCount: number;
  availableTables: number;
  totalAvailableSeats: number;
}

interface RunConfigurationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRunCreated: (runData: any) => void;
  preSelectedRuleIds?: string[] | null;
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.4)',
    zIndex: 1200,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modal: {
    backgroundColor: BG_CARD,
    borderRadius: '8px',
    padding: '1.5rem',
    width: '600px',
    maxWidth: '90vw',
    maxHeight: '80vh',
    overflowY: 'auto',
    boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.25rem',
  },
  title: {
    fontSize: '1rem',
    fontFamily: FONT_HEADING,
    fontWeight: 700,
    color: TEXT_DARK,
    margin: 0,
  },
  closeBtn: {
    border: 'none',
    background: 'transparent',
    fontSize: '1.2rem',
    cursor: 'pointer',
    color: '#666',
    padding: '4px',
  },
  stepIndicator: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '1.25rem',
    alignItems: 'center',
  },
  stepDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: BORDER_LIGHT,
  },
  stepDotActive: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: PRIMARY,
  },
  stepLabel: {
    fontSize: '0.72rem',
    color: TEXT_MUTED,
    marginLeft: '0.25rem',
  },
  section: {
    marginBottom: '1rem',
  },
  sectionTitle: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: TEXT_DARK,
    marginBottom: '0.5rem',
  },
  radioGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.4rem',
    marginBottom: '0.75rem',
  },
  radioLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: '0.82rem',
    color: TEXT_DARK,
    cursor: 'pointer',
  },
  checkboxList: {
    maxHeight: '200px',
    overflowY: 'auto' as const,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '4px',
    padding: '0.5rem',
  },
  checkboxItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.3rem 0.25rem',
    fontSize: '0.8rem',
    color: TEXT_DARK,
  },
  summaryBox: {
    padding: '0.75rem 1rem',
    backgroundColor: BG_SURFACE,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '6px',
    marginBottom: '0.75rem',
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.25rem 0',
    fontSize: '0.82rem',
    color: TEXT_DARK,
  },
  summaryValue: {
    fontWeight: 600,
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '1.25rem',
    paddingTop: '1rem',
    borderTop: `1px solid ${BORDER_LIGHT}`,
  },
  nextBtn: {
    padding: '0.5rem 1.1rem',
    fontSize: '0.82rem',
    fontWeight: 600,
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  backBtn: {
    padding: '0.5rem 1.1rem',
    fontSize: '0.82rem',
    fontWeight: 500,
    backgroundColor: '#e0e0e0',
    color: '#333',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  skipBtn: {
    padding: '0.5rem 1.1rem',
    fontSize: '0.82rem',
    fontWeight: 500,
    backgroundColor: 'transparent',
    color: TEXT_MUTED,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '4px',
    cursor: 'pointer',
  },
  executeBtn: {
    padding: '0.55rem 1.2rem',
    fontSize: '0.85rem',
    fontWeight: 600,
    backgroundColor: '#2e7d32',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  error: {
    padding: '0.5rem 0.75rem',
    color: '#d32f2f',
    fontSize: '0.8rem',
    backgroundColor: '#fdecea',
    borderRadius: '4px',
    marginBottom: '0.75rem',
  },
  loading: {
    textAlign: 'center' as const,
    padding: '1.5rem',
    color: TEXT_MUTED,
    fontSize: '0.85rem',
  },
};

export function RunConfigurationModal({ isOpen, onClose, onRunCreated, preSelectedRuleIds }: RunConfigurationModalProps) {
  const [step, setStep] = useState(1);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Step 1 state
  const [guestSelectionMode, setGuestSelectionMode] = useState<'AllUnseated' | 'SelectedOrders'>('AllUnseated');
  const [orders, setOrders] = useState<Order[]>([]);
  const [selectedOrderIds, setSelectedOrderIds] = useState<string[]>([]);
  const [rules, setRules] = useState<Rule[]>([]);
  const [selectedRuleIds, setSelectedRuleIds] = useState<string[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [familyGroups, setFamilyGroups] = useState<FamilyGroupOption[]>([]);
  const [excludedFamilyGroupIds, setExcludedFamilyGroupIds] = useState<string[]>([]);

  // Step 2 state
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [excludedGuestIds, setExcludedGuestIds] = useState<string[]>([]);

  // Fetch rules and family groups on open
  useEffect(() => {
    if (!isOpen) return;
    setStep(1);
    setError('');
    setPreview(null);
    setExcludedGuestIds([]);
    setExcludedFamilyGroupIds([]);

    apiClient<Rule[]>('/batch-assignment/rules')
      .then((data) => {
        setRules(data);
        if (preSelectedRuleIds && preSelectedRuleIds.length > 0) {
          setSelectedRuleIds(preSelectedRuleIds);
        } else {
          setSelectedRuleIds(data.filter((r) => r.enabled).map((r) => r.id));
        }
      })
      .catch(() => setError('Failed to load rules.'));

    apiClient<FamilyGroupOption[]>('/batch-assignment/family-groups')
      .then((data) => {
        setFamilyGroups(data.filter((g) => g.active));
      })
      .catch(() => { /* silently ignore — family groups are optional */ });
  }, [isOpen]);

  // Fetch orders when SelectedOrders mode is chosen
  useEffect(() => {
    if (guestSelectionMode !== 'SelectedOrders') return;
    if (orders.length > 0) return;
    setOrdersLoading(true);
    apiClient<Order[]>('/legacy-ball/orders')
      .then((data) => {
        setOrders(data);
      })
      .catch(() => setError('Failed to load orders.'))
      .finally(() => setOrdersLoading(false));
  }, [guestSelectionMode, orders.length]);

  const handleToggleOrder = (orderId: string) => {
    setSelectedOrderIds((prev) =>
      prev.includes(orderId)
        ? prev.filter((id) => id !== orderId)
        : [...prev, orderId],
    );
  };

  const handleToggleRule = (ruleId: string) => {
    setSelectedRuleIds((prev) =>
      prev.includes(ruleId)
        ? prev.filter((id) => id !== ruleId)
        : [...prev, ruleId],
    );
  };

  const handleToggleFamilyGroup = (groupId: string) => {
    setExcludedFamilyGroupIds((prev) =>
      prev.includes(groupId)
        ? prev.filter((id) => id !== groupId)
        : [...prev, groupId],
    );
  };

  const handleToggleExclude = (guestId: string) => {
    setExcludedGuestIds((prev) =>
      prev.includes(guestId)
        ? prev.filter((id) => id !== guestId)
        : [...prev, guestId],
    );
  };

  const canProceedStep1 = selectedRuleIds.length > 0;

  const handleNextStep1 = async () => {
    if (!canProceedStep1) return;
    setError('');
    setLoading(true);

    try {
      const body: any = {
        guestSelectionMode,
        ruleIds: selectedRuleIds,
      };
      if (guestSelectionMode === 'SelectedOrders') {
        body.selectedOrderIds = selectedOrderIds;
      }
      // Exclude members of unchecked family groups
      if (excludedFamilyGroupIds.length > 0) {
        const excludedMemberIds = familyGroups
          .filter((g) => excludedFamilyGroupIds.includes(g.id))
          .flatMap((g) => g.members.map((m) => m.id));
        if (excludedMemberIds.length > 0) {
          body.excludedGuestIds = excludedMemberIds;
        }
      }

      const data = await apiClient<PreviewData>('/batch-assignment/runs/preview', {
        method: 'POST',
        body,
      });
      setPreview(data);
      setStep(2);
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr?.message || 'Failed to generate preview.');
    } finally {
      setLoading(false);
    }
  };

  const handleNextStep2 = () => {
    setStep(3);
  };

  const handleExecute = async () => {
    setError('');
    setLoading(true);

    try {
      const body: any = {
        guestSelectionMode,
        ruleIds: selectedRuleIds,
      };
      if (guestSelectionMode === 'SelectedOrders') {
        body.selectedOrderIds = selectedOrderIds;
      }
      // Combine manually excluded guests and family group member exclusions
      const familyExcludedIds = familyGroups
        .filter((g) => excludedFamilyGroupIds.includes(g.id))
        .flatMap((g) => g.members.map((m) => m.id));
      const allExcluded = [...new Set([...excludedGuestIds, ...familyExcludedIds])];
      if (allExcluded.length > 0) {
        body.excludedGuestIds = allExcluded;
      }

      const runData = await apiClient('/batch-assignment/runs', {
        method: 'POST',
        body,
      });
      onRunCreated(runData);
    } catch (err: unknown) {
      const apiErr = err as { statusCode?: number; message?: string };
      if (apiErr?.statusCode === 409) {
        setError('A run is already pending. Approve or reject it before starting a new one.');
      } else {
        setError(apiErr?.message || 'Failed to execute batch assignment.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={styles.overlay}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-label="Batch Assignment Configuration"
    >
      <div style={styles.modal}>
        <div style={styles.header}>
          <h3 style={styles.title}>
            {step === 1 && 'Configure Batch Assignment'}
            {step === 2 && 'Review Scope'}
            {step === 3 && 'Confirm & Execute'}
          </h3>
          <button style={styles.closeBtn} onClick={onClose} aria-label="Close modal">✕</button>
        </div>

        {/* Step indicator */}
        <div style={styles.stepIndicator}>
          <span style={step >= 1 ? styles.stepDotActive : styles.stepDot} />
          <span style={styles.stepLabel}>Scope</span>
          <span style={step >= 2 ? styles.stepDotActive : styles.stepDot} />
          <span style={styles.stepLabel}>Review</span>
          <span style={step >= 3 ? styles.stepDotActive : styles.stepDot} />
          <span style={styles.stepLabel}>Execute</span>
        </div>

        {error && <div style={styles.error} role="alert">{error}</div>}

        {/* Step 1: Guest Scope + Rule Selection */}
        {step === 1 && (
          <>
            <div style={styles.section}>
              <div style={styles.sectionTitle}>Guest Scope</div>
              <div style={styles.radioGroup}>
                <label style={styles.radioLabel}>
                  <input
                    type="radio"
                    name="guestScope"
                    checked={guestSelectionMode === 'AllUnseated'}
                    onChange={() => setGuestSelectionMode('AllUnseated')}
                  />
                  All Unseated Guests
                </label>
                <label style={styles.radioLabel}>
                  <input
                    type="radio"
                    name="guestScope"
                    checked={guestSelectionMode === 'SelectedOrders'}
                    onChange={() => setGuestSelectionMode('SelectedOrders')}
                  />
                  Selected Orders
                </label>
              </div>

              {guestSelectionMode === 'SelectedOrders' && (
                <>
                  {ordersLoading ? (
                    <div style={styles.loading}>Loading orders...</div>
                  ) : (
                    <div style={styles.checkboxList}>
                      {orders.map((order) => (
                        <label key={order.id} style={styles.checkboxItem}>
                          <input
                            type="checkbox"
                            checked={selectedOrderIds.includes(order.id)}
                            onChange={() => handleToggleOrder(order.id)}
                          />
                          {order.purchaserName} ({order.referenceId}) — {order.ticketCount} tickets
                        </label>
                      ))}
                      {orders.length === 0 && (
                        <div style={{ fontSize: '0.8rem', color: TEXT_MUTED, padding: '0.5rem' }}>
                          No orders found.
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            <div style={styles.section}>
              <div style={styles.sectionTitle}>Rules to Apply</div>
              <div style={styles.checkboxList}>
                {rules.map((rule) => (
                  <label key={rule.id} style={styles.checkboxItem}>
                    <input
                      type="checkbox"
                      checked={selectedRuleIds.includes(rule.id)}
                      onChange={() => handleToggleRule(rule.id)}
                    />
                    <span style={{ fontWeight: 500 }}>{rule.name}</span>
                    <span style={{
                      fontSize: '0.68rem',
                      color: '#1565c0',
                      backgroundColor: '#e3f2fd',
                      padding: '0.1rem 0.3rem',
                      borderRadius: '3px',
                      marginLeft: '0.35rem',
                    }}>
                      {rule.ruleType}
                    </span>
                  </label>
                ))}
                {rules.length === 0 && (
                  <div style={{ fontSize: '0.8rem', color: TEXT_MUTED, padding: '0.5rem' }}>
                    No rules configured. Create rules first.
                  </div>
                )}
              </div>
            </div>

            {familyGroups.length > 0 && (
              <div style={styles.section}>
                <div style={styles.sectionTitle}>
                  Family Groups
                  <span style={{ fontSize: '0.72rem', fontWeight: 400, color: TEXT_MUTED, marginLeft: '0.5rem' }}>
                    Uncheck to exclude from run
                  </span>
                </div>
                <div style={styles.checkboxList}>
                  {familyGroups.map((group) => (
                    <label key={group.id} style={styles.checkboxItem}>
                      <input
                        type="checkbox"
                        checked={!excludedFamilyGroupIds.includes(group.id)}
                        onChange={() => handleToggleFamilyGroup(group.id)}
                      />
                      <span style={{ fontWeight: 500 }}>{group.name}</span>
                      <span style={{
                        fontSize: '0.68rem',
                        color: TEXT_MUTED,
                        marginLeft: '0.35rem',
                      }}>
                        ({group.memberCount} member{group.memberCount !== 1 ? 's' : ''})
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div style={styles.footer}>
              <button style={styles.backBtn} onClick={onClose}>Cancel</button>
              <button
                style={{ ...styles.nextBtn, opacity: canProceedStep1 ? 1 : 0.5 }}
                onClick={handleNextStep1}
                disabled={!canProceedStep1 || loading}
              >
                {loading ? 'Loading...' : 'Next →'}
              </button>
            </div>
          </>
        )}

        {/* Step 2: Accessibility Review / Preview */}
        {step === 2 && preview && (
          <>
            <div style={styles.section}>
              <div style={styles.sectionTitle}>Scope Summary</div>
              <div style={styles.summaryBox}>
                <div style={styles.summaryRow}>
                  <span>Guests in scope</span>
                  <span style={styles.summaryValue}>{preview.totalGuestsInScope}</span>
                </div>
                <div style={styles.summaryRow}>
                  <span>Available tables</span>
                  <span style={styles.summaryValue}>{preview.availableTables}</span>
                </div>
                <div style={styles.summaryRow}>
                  <span>Available seats</span>
                  <span style={styles.summaryValue}>{preview.totalAvailableSeats}</span>
                </div>
                <div style={styles.summaryRow}>
                  <span>Order groups</span>
                  <span style={styles.summaryValue}>{preview.orderGroupsCount}</span>
                </div>
                <div style={styles.summaryRow}>
                  <span>Family groups</span>
                  <span style={styles.summaryValue}>{preview.familyGroupsCount}</span>
                </div>
              </div>
            </div>

            {preview.guests.length > 0 && (
              <div style={styles.section}>
                <div style={styles.sectionTitle}>
                  Guests in Scope ({preview.guests.length})
                  <span style={{ fontSize: '0.72rem', fontWeight: 400, color: TEXT_MUTED, marginLeft: '0.5rem' }}>
                    Uncheck to exclude
                  </span>
                </div>
                <div style={styles.checkboxList}>
                  {preview.guests.map((guest) => (
                    <label key={guest.id} style={styles.checkboxItem}>
                      <input
                        type="checkbox"
                        checked={!excludedGuestIds.includes(guest.id)}
                        onChange={() => handleToggleExclude(guest.id)}
                      />
                      {guest.fullName}
                      {guest.ticketNumber && (
                        <span style={{ fontSize: '0.72rem', color: TEXT_MUTED }}>
                          ({guest.ticketNumber})
                        </span>
                      )}
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div style={styles.footer}>
              <button style={styles.backBtn} onClick={() => setStep(1)}>← Back</button>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {preview.guests.length === 0 && (
                  <button style={styles.skipBtn} onClick={handleNextStep2}>Skip</button>
                )}
                <button style={styles.nextBtn} onClick={handleNextStep2}>
                  Next →
                </button>
              </div>
            </div>
          </>
        )}

        {/* Step 3: Confirm & Execute */}
        {step === 3 && (
          <>
            <div style={styles.section}>
              <div style={styles.sectionTitle}>Configuration Summary</div>
              <div style={styles.summaryBox}>
                <div style={styles.summaryRow}>
                  <span>Guest scope</span>
                  <span style={styles.summaryValue}>
                    {guestSelectionMode === 'AllUnseated' ? 'All Unseated Guests' : `${selectedOrderIds.length} Selected Orders`}
                  </span>
                </div>
                <div style={styles.summaryRow}>
                  <span>Rules applied</span>
                  <span style={styles.summaryValue}>{selectedRuleIds.length}</span>
                </div>
                {excludedGuestIds.length > 0 && (
                  <div style={styles.summaryRow}>
                    <span>Excluded guests</span>
                    <span style={styles.summaryValue}>{excludedGuestIds.length}</span>
                  </div>
                )}
                {preview && (
                  <>
                    <div style={styles.summaryRow}>
                      <span>Guests to process</span>
                      <span style={styles.summaryValue}>
                        {preview.totalGuestsInScope - excludedGuestIds.length}
                      </span>
                    </div>
                    <div style={styles.summaryRow}>
                      <span>Available seats</span>
                      <span style={styles.summaryValue}>{preview.totalAvailableSeats}</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div style={{
              padding: '0.75rem',
              backgroundColor: '#fff8e1',
              border: '1px solid #ffcc80',
              borderRadius: '4px',
              fontSize: '0.8rem',
              color: '#e65100',
              marginBottom: '1rem',
            }}>
              This will generate seat assignments based on the selected rules. You will be able to review
              and approve/reject the results before they are applied.
            </div>

            <div style={styles.footer}>
              <button style={styles.backBtn} onClick={() => setStep(2)}>← Back</button>
              <button
                style={{ ...styles.executeBtn, opacity: loading ? 0.7 : 1 }}
                onClick={handleExecute}
                disabled={loading}
              >
                {loading ? 'Executing...' : 'Execute Batch Assignment'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
